/**
 * Skill Creator V2 - New skill creation system with type-specific sliders
 * Supports 8 delivery types, each with unique parameters and trade-offs
 * All sliders use concrete values with specific units (px, adet, saniye, etc.)
 */

import { ElementType } from '@shared/types';
import { EffectStone } from './SkillCreator';
import {
  SkillDeliveryType,
  TypeSpecificSliders,
  ProjectileSliders,
  AOESliders,
  MeleeSliders,
  BeamSliders,
  AuraSliders,
  BuffSliders,
  TrapSliders,
  TotemSliders,
  CurseSliders,
  MinionSliders,
  DELIVERY_BASE_PARAMS,
  getDefaultSliders,
  DELIVERY_TYPE_SLIDERS,
  AOEShape,
} from './SkillDeliveryTypes';

// ========================
// Custom Skill V2 Interface
// ========================

export interface CustomSkillV2 {
  id: string;
  name: string;
  version: 2;
  deliveryType: SkillDeliveryType;
  element: ElementType;
  sliders: TypeSpecificSliders;

  // Calculated costs (varies by delivery type)
  manaCost: number;
  cooldown: number;
  manaDrainPerSecond?: number;  // Beam only - mana cost while channeling
  manaReservePercent?: number;  // Aura only - percentage of max mana reserved
  manaReserveFlat?: number;     // Minion only - flat mana reserved

  // Socket slots for effect stones
  socketCount: number;
  sockets: (EffectStone | null)[];

  // Type-specific choices (non-slider selections)
  meleeType?: import('@shared/skills/SkillDeliveryTypes').MeleeType;
  projectileContact?: import('@shared/skills/SkillDeliveryTypes').ProjectileContact;
  aoeTrigger?: import('@shared/skills/SkillDeliveryTypes').AOETrigger;
  aoePosition?: import('@shared/skills/SkillDeliveryTypes').AOEPosition;
  beamMode?: import('@shared/skills/SkillDeliveryTypes').BeamMode;
  buffType?: import('@shared/skills/SkillDeliveryTypes').BuffType;
  trapTrigger?: import('@shared/skills/SkillDeliveryTypes').TrapTrigger;
  totemType?: import('@shared/skills/SkillDeliveryTypes').TotemType;
  curseType?: import('@shared/skills/SkillDeliveryTypes').CurseType;
  curseTarget?: import('@shared/skills/SkillDeliveryTypes').CurseTarget;
  minionType?: import('@shared/skills/SkillDeliveryTypes').MinionType;

  // Calculated modifiers
  _thrustDamageMult?: number;  // Thrust: damage multiplier based on thrust range

  // Runtime state
  currentCooldown: number;
  level: number;
  xp: number;       // Current skill XP
  maxXp: number;    // XP needed for next level
  isActive?: boolean;  // For auras and channeled skills
}

// ========================
// Trade-off System
// ========================

// Trade-off config: how much each slider contributes to mana/cooldown cost
// manaCostWeight: mana cost added per normalized slider unit (0-1 range)
// cooldownWeight: cooldown added per normalized slider unit
interface TradeOffConfig {
  manaCostWeight: number;   // Additional mana cost when slider is at max
  cooldownWeight: number;   // Additional cooldown when slider is at max
}

// Helper: normalize a slider value to 0-1 range based on its config
function getNormalizedSliderValue(sliderKey: string, value: number, deliveryType: SkillDeliveryType): number {
  const configs = DELIVERY_TYPE_SLIDERS[deliveryType];
  const config = configs.find(c => c.key === sliderKey);
  if (!config) return 0;
  return (value - config.min) / (config.max - config.min);
}

// Per-type trade-off weights: how much each slider adds to cost
// Values represent mana/cooldown ADDED when slider goes from min to max

const PROJECTILE_TRADE_OFFS: Record<string, TradeOffConfig> = {
  damage: { manaCostWeight: 6, cooldownWeight: 0.3 },
  projectileCount: { manaCostWeight: 10, cooldownWeight: 0.4 },
  speed: { manaCostWeight: 2, cooldownWeight: 0 },
  spreadAngle: { manaCostWeight: 1, cooldownWeight: 0 },
  range: { manaCostWeight: 3, cooldownWeight: 0.1 },
  thickness: { manaCostWeight: 1, cooldownWeight: 0 },
  pierce: { manaCostWeight: 4, cooldownWeight: 0.2 },
  chain: { manaCostWeight: 5, cooldownWeight: 0.2 },
};

const AOE_TRADE_OFFS: Record<string, TradeOffConfig> = {
  damage: { manaCostWeight: 10, cooldownWeight: 0.8 },
  radius: { manaCostWeight: 8, cooldownWeight: 0.5 },
  duration: { manaCostWeight: 6, cooldownWeight: 0.5 },
  coneAngle: { manaCostWeight: 2, cooldownWeight: 0.1 },
  delay: { manaCostWeight: -2, cooldownWeight: -0.2 },  // longer delay = cheaper
  range: { manaCostWeight: 4, cooldownWeight: 0.2 },
};

const MELEE_TRADE_OFFS: Record<string, TradeOffConfig> = {
  damage: { manaCostWeight: 4, cooldownWeight: 0.3 },
  range: { manaCostWeight: 2, cooldownWeight: 0.1 },
  thrustRange: { manaCostWeight: 5, cooldownWeight: 0.2 },
};

// Beam and Aura use direct slider values for costs (manaDrain, manaReserve)
// so they don't need trade-off configs.

const BUFF_TRADE_OFFS: Record<string, TradeOffConfig> = {
  effectStrength: { manaCostWeight: 6, cooldownWeight: 1.0 },
  charges: { manaCostWeight: 4, cooldownWeight: 0.5 },
  hpCost: { manaCostWeight: -2, cooldownWeight: -0.5 },  // Higher HP cost = cheaper mana
  duration: { manaCostWeight: 4, cooldownWeight: 0.8 },
  shield: { manaCostWeight: 8, cooldownWeight: 1.0 },
  shieldDecay: { manaCostWeight: -2, cooldownWeight: -0.3 },  // Faster decay = cheaper
  leech: { manaCostWeight: 5, cooldownWeight: 0.8 },
  cooldown: { manaCostWeight: -3, cooldownWeight: 0 },  // Longer CD = cheaper mana (direct mapping)
};

const TRAP_TRADE_OFFS: Record<string, TradeOffConfig> = {
  damage: { manaCostWeight: 5, cooldownWeight: 0.3 },
  triggerRadius: { manaCostWeight: 2, cooldownWeight: 0 },
  trapCount: { manaCostWeight: 8, cooldownWeight: 0.5 },
  armTime: { manaCostWeight: -1, cooldownWeight: -0.1 },  // Longer arm = cheaper
};

const TOTEM_TRADE_OFFS: Record<string, TradeOffConfig> = {
  totemHealth: { manaCostWeight: 6, cooldownWeight: 1.0 },
  damage: { manaCostWeight: 5, cooldownWeight: 0.5 },
  range: { manaCostWeight: 3, cooldownWeight: 0.3 },
};

const CURSE_TRADE_OFFS: Record<string, TradeOffConfig> = {
  power: { manaCostWeight: 1.5, cooldownWeight: 1.3 },
  duration: { manaCostWeight: 1.3, cooldownWeight: 1.2 },
  area: { manaCostWeight: 1.4, cooldownWeight: 1.1 },
};

const MINION_TRADE_OFFS: Record<string, TradeOffConfig> = {
  minionHealth: { manaCostWeight: 1.3, cooldownWeight: 1.0 },
  minionDamage: { manaCostWeight: 1.4, cooldownWeight: 1.0 },
  manaReserve: { manaCostWeight: 1.0, cooldownWeight: 1.0 },
};

// ========================
// Skill Creator V2 Class
// ========================

export class SkillCreatorV2 {
  /**
   * Create a new V2 skill
   */
  static createSkill(
    deliveryType: SkillDeliveryType,
    element: ElementType,
    name: string
  ): CustomSkillV2 {
    const baseParams = DELIVERY_BASE_PARAMS[deliveryType];
    const defaultSliders = getDefaultSliders(deliveryType);

    const skill: CustomSkillV2 = {
      id: `skill_v2_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`,
      name,
      version: 2,
      deliveryType,
      element,
      sliders: defaultSliders,
      manaCost: baseParams.baseManaCost,
      cooldown: baseParams.baseCooldown,
      socketCount: 2,
      sockets: [null, null],
      currentCooldown: 0,
      level: 1,
      xp: 0,
      maxXp: 10,
    };

    // Set type-specific default choices and mana properties
    switch (deliveryType) {
      case 'melee': skill.meleeType = 'strike'; break;
      case 'projectile': skill.projectileContact = 'none'; break;
      case 'aoe': skill.aoeTrigger = 'instant'; skill.aoePosition = 'target'; break;
      case 'beam': skill.beamMode = 'steady'; skill.manaDrainPerSecond = baseParams.baseManaDrain; break;
      case 'buff': skill.buffType = 'warcry'; break;
      case 'trap': skill.trapTrigger = 'proximity'; break;
      case 'totem': skill.totemType = 'projectile'; break;
      case 'curse': skill.curseType = 'weaken'; skill.curseTarget = 'area'; break;
      case 'minion': skill.minionType = 'melee'; skill.manaReserveFlat = (defaultSliders as MinionSliders).manaReserve; break;
      case 'aura': skill.manaReservePercent = baseParams.baseManaReserve; break;
    }

    // Calculate initial costs
    this.recalculateCosts(skill);

    return skill;
  }

  /**
   * Adjust a slider value - clamps to the slider's concrete min/max range
   */
  static adjustSlider(
    skill: CustomSkillV2,
    sliderKey: string,
    value: number
  ): void {
    const sliders = skill.sliders as Record<string, number>;
    if (sliderKey in sliders) {
      // Find the slider config for proper min/max clamping
      const configs = DELIVERY_TYPE_SLIDERS[skill.deliveryType];
      const config = configs.find(c => c.key === sliderKey);
      if (config) {
        sliders[sliderKey] = Math.max(config.min, Math.min(config.max, value));
      } else {
        sliders[sliderKey] = value;
      }
      this.recalculateCosts(skill);
    }
  }

  /**
   * Recalculate mana cost and cooldown based on concrete slider values
   */
  static recalculateCosts(skill: CustomSkillV2): void {
    const baseParams = DELIVERY_BASE_PARAMS[skill.deliveryType];

    switch (skill.deliveryType) {
      case 'projectile':
        this.recalculateProjectile(skill, baseParams);
        break;
      case 'aoe':
        this.recalculateAOE(skill, baseParams);
        break;
      case 'melee':
        this.recalculateMelee(skill, baseParams);
        break;
      case 'beam':
        this.recalculateBeam(skill, baseParams);
        break;
      case 'aura':
        this.recalculateAura(skill, baseParams);
        break;
      case 'buff':
        this.recalculateBuff(skill, baseParams);
        break;
      case 'trap':
        this.recalculateTrap(skill, baseParams);
        break;
      case 'totem':
        this.recalculateTotem(skill, baseParams);
        break;
      case 'curse':
        this.recalculateCurse(skill, baseParams);
        break;
      case 'minion':
        this.recalculateMinion(skill, baseParams);
        break;
    }

    // Apply effect stone modifiers (additive stacking)
    let totalCDR = 0;
    let totalManaCostReduction = 0;
    for (const stone of skill.sockets) {
      if (stone?.effect.cooldownReduction) {
        totalCDR += stone.effect.cooldownReduction;
      }
      if (stone?.effect.manaCostReduction) {
        totalManaCostReduction += stone.effect.manaCostReduction;
      }
    }
    if (totalCDR > 0) {
      skill.cooldown *= 1 - Math.min(80, totalCDR) / 100;
    }
    if (totalManaCostReduction > 0) {
      const reduction = 1 - Math.min(80, totalManaCostReduction) / 100;
      skill.manaCost *= reduction;
      if (skill.manaDrainPerSecond) {
        skill.manaDrainPerSecond *= reduction;
      }
    }

    // Round values
    skill.manaCost = Math.max(1, Math.floor(skill.manaCost));
    skill.cooldown = Math.max(0.1, Math.round(skill.cooldown * 100) / 100);
    if (skill.manaDrainPerSecond) {
      skill.manaDrainPerSecond = Math.max(1, Math.floor(skill.manaDrainPerSecond));
    }
    if (skill.manaReservePercent) {
      skill.manaReservePercent = Math.max(5, Math.min(60, Math.floor(skill.manaReservePercent)));
    }
    if (skill.manaReserveFlat !== undefined) {
      skill.manaReserveFlat = Math.max(5, Math.floor(skill.manaReserveFlat));
    }
  }

  /** Calculate cost from slider values using additive trade-off weights */
  private static calculateCostFromSliders(
    skill: CustomSkillV2,
    sliders: Record<string, number>,
    tradeOffs: Record<string, TradeOffConfig>,
    baseMana: number,
    baseCooldown: number
  ): { manaCost: number; cooldown: number } {
    let manaCost = baseMana;
    let cooldown = baseCooldown;

    for (const [key, val] of Object.entries(sliders)) {
      if (typeof val !== 'number') continue;
      const tradeOff = tradeOffs[key];
      if (!tradeOff) continue;

      const norm = getNormalizedSliderValue(key, val, skill.deliveryType);
      manaCost += norm * tradeOff.manaCostWeight;
      cooldown += norm * tradeOff.cooldownWeight;
    }

    return { manaCost: Math.max(1, manaCost), cooldown: Math.max(0.1, cooldown) };
  }

  private static recalculateProjectile(skill: CustomSkillV2, baseParams: typeof DELIVERY_BASE_PARAMS.projectile): void {
    const s = skill.sliders as ProjectileSliders;
    const result = this.calculateCostFromSliders(
      skill, s as unknown as Record<string, number>,
      PROJECTILE_TRADE_OFFS, baseParams.baseManaCost, baseParams.baseCooldown
    );
    skill.manaCost = result.manaCost;
    skill.cooldown = result.cooldown;
  }

  private static recalculateAOE(skill: CustomSkillV2, baseParams: typeof DELIVERY_BASE_PARAMS.aoe): void {
    const s = skill.sliders as AOESliders;
    const numericSliders: Record<string, number> = {};
    for (const [key, val] of Object.entries(s)) {
      if (typeof val === 'number') numericSliders[key] = val;
    }
    const result = this.calculateCostFromSliders(
      skill, numericSliders,
      AOE_TRADE_OFFS, baseParams.baseManaCost, baseParams.baseCooldown
    );
    skill.manaCost = result.manaCost;
    skill.cooldown = result.cooldown;
  }

  private static recalculateMelee(skill: CustomSkillV2, baseParams: typeof DELIVERY_BASE_PARAMS.melee): void {
    const s = skill.sliders as MeleeSliders;
    const result = this.calculateCostFromSliders(
      skill, s as unknown as Record<string, number>,
      MELEE_TRADE_OFFS, baseParams.baseManaCost, baseParams.baseCooldown
    );
    skill.manaCost = result.manaCost;
    skill.cooldown = result.cooldown;

    // Thrust: more range = less damage (inverse relationship)
    if (skill.meleeType === 'thrust') {
      const thrustNorm = getNormalizedSliderValue('thrustRange', s.thrustRange, 'melee'); // 0-1
      // At max thrust range, damage is reduced to 40%; at min range, full damage
      skill._thrustDamageMult = 1.0 - thrustNorm * 0.6;
    } else {
      skill._thrustDamageMult = 1.0;
    }
  }

  private static recalculateBeam(skill: CustomSkillV2, _baseParams: typeof DELIVERY_BASE_PARAMS.beam): void {
    const s = skill.sliders as BeamSliders;
    // Beam: no upfront mana cost, mana drain comes directly from the manaDrain slider
    skill.manaCost = 0;
    skill.cooldown = 0.1;
    skill.manaDrainPerSecond = s.manaDrain;
  }

  private static recalculateAura(skill: CustomSkillV2, _baseParams: typeof DELIVERY_BASE_PARAMS.aura): void {
    const s = skill.sliders as AuraSliders;
    // Aura: mana reserve comes directly from the manaReserve slider
    skill.manaCost = 0;
    skill.cooldown = 0.5; // Toggle cooldown
    skill.manaReservePercent = s.manaReserve;
  }

  private static recalculateBuff(skill: CustomSkillV2, baseParams: typeof DELIVERY_BASE_PARAMS.buff): void {
    const s = skill.sliders as BuffSliders;
    const result = this.calculateCostFromSliders(
      skill, s as unknown as Record<string, number>,
      BUFF_TRADE_OFFS, baseParams.baseManaCost, baseParams.baseCooldown
    );
    skill.manaCost = result.manaCost;
    // Buff cooldown comes directly from the cooldown slider
    skill.cooldown = s.cooldown;
  }

  private static recalculateTrap(skill: CustomSkillV2, baseParams: typeof DELIVERY_BASE_PARAMS.trap): void {
    const s = skill.sliders as TrapSliders;
    const result = this.calculateCostFromSliders(
      skill, s as unknown as Record<string, number>,
      TRAP_TRADE_OFFS, baseParams.baseManaCost, baseParams.baseCooldown
    );
    skill.manaCost = result.manaCost;
    skill.cooldown = result.cooldown;
  }

  private static recalculateTotem(skill: CustomSkillV2, baseParams: typeof DELIVERY_BASE_PARAMS.totem): void {
    const s = skill.sliders as TotemSliders;
    const result = this.calculateCostFromSliders(
      skill, s as unknown as Record<string, number>,
      TOTEM_TRADE_OFFS, baseParams.baseManaCost, baseParams.baseCooldown
    );
    skill.manaCost = result.manaCost;
    skill.cooldown = result.cooldown;
  }

  private static recalculateCurse(skill: CustomSkillV2, baseParams: typeof DELIVERY_BASE_PARAMS.curse): void {
    const s = skill.sliders as CurseSliders;
    const result = this.calculateCostFromSliders(
      skill, s as unknown as Record<string, number>,
      CURSE_TRADE_OFFS, baseParams.baseManaCost, baseParams.baseCooldown
    );
    skill.manaCost = result.manaCost;
    skill.cooldown = result.cooldown;
  }

  private static recalculateMinion(skill: CustomSkillV2, baseParams: typeof DELIVERY_BASE_PARAMS.minion): void {
    const s = skill.sliders as MinionSliders;
    // Minion: no upfront mana cost, flat mana reserve comes directly from the slider
    // Use trade-offs only for cooldown scaling (summon/dismiss toggle)
    const result = this.calculateCostFromSliders(
      skill, s as unknown as Record<string, number>,
      MINION_TRADE_OFFS, baseParams.baseManaCost, baseParams.baseCooldown
    );
    skill.manaCost = 0; // Override: minion has no upfront mana cost
    skill.cooldown = result.cooldown;
    skill.manaReserveFlat = s.manaReserve;
  }

  // ========================
  // Effective Value Getters
  // ========================
  // All getters return the slider value directly (concrete units).
  // Level scaling and effect stone bonuses are applied on top.

  /**
   * Get effective damage (flat value from slider + level scaling + stone bonuses)
   */
  static getEffectiveDamage(skill: CustomSkillV2): number {
    // Curse has no direct damage
    if (skill.deliveryType === 'curse') return 0;

    // Minion returns minion damage
    if (skill.deliveryType === 'minion') {
      const s = skill.sliders as MinionSliders;
      let minionDmg = s.minionDamage;
      minionDmg *= 1 + (skill.level - 1) * 0.085;
      for (const stone of skill.sockets) {
        if (stone?.effect.damageBonus) {
          minionDmg *= 1 + stone.effect.damageBonus / 100;
        }
      }
      return minionDmg;
    }

    const sliders = skill.sliders as Record<string, number>;

    // Get the raw damage value directly from the slider
    let baseDamage = sliders['damage'] ?? sliders['dps'] ?? 15;

    // Level scaling: 8.5% per level
    baseDamage *= 1 + (skill.level - 1) * 0.085;

    // AOE cone shape gets bonus damage (smaller effective area)
    if (skill.deliveryType === 'aoe') {
      const s = skill.sliders as AOESliders;
      if (s.shape === 'cone') {
        baseDamage *= 1.25;
      }
    }

    // Effect stone bonuses
    for (const stone of skill.sockets) {
      if (stone?.effect.damageBonus) {
        baseDamage *= 1 + stone.effect.damageBonus / 100;
      }
    }

    return baseDamage;
  }

  /**
   * Get effective area/radius (alias for getEffectiveRadius for V1 compatibility)
   */
  static getEffectiveArea(skill: CustomSkillV2): number {
    return this.getEffectiveRadius(skill);
  }

  /**
   * Get effective element (can be overridden by stones)
   */
  static getEffectiveElement(skill: CustomSkillV2): ElementType {
    for (const stone of skill.sockets) {
      if (stone?.effect.elementOverride) {
        return stone.effect.elementOverride;
      }
    }
    return skill.element;
  }

  /**
   * Get effective radius (px) - direct from slider
   */
  static getEffectiveRadius(skill: CustomSkillV2): number {
    const sliders = skill.sliders as Record<string, number>;
    let radius: number;

    switch (skill.deliveryType) {
      case 'aoe':
        radius = sliders['radius'] ?? 80;
        break;
      case 'trap':
        radius = sliders['triggerRadius'] ?? 40;
        break;
      case 'melee':
        radius = sliders['range'] ?? 50;
        break;
      default:
        radius = sliders['radius'] ?? 0;
        break;
    }

    // Effect stone bonuses
    for (const stone of skill.sockets) {
      if (stone?.effect.areaBonus) {
        radius *= 1 + stone.effect.areaBonus / 100;
      }
    }

    return Math.floor(radius);
  }

  /**
   * Get effective range (px) - direct from slider
   */
  static getEffectiveRange(skill: CustomSkillV2): number {
    const sliders = skill.sliders as Record<string, number>;

    switch (skill.deliveryType) {
      case 'projectile':
        return sliders['range'] ?? 200;
      case 'aoe':
        return sliders['range'] ?? 150;
      case 'melee':
        return sliders['range'] ?? 50;
      case 'beam':
        return sliders['range'] ?? 160;
      case 'totem':
        return sliders['range'] ?? 120;
      case 'trap':
        return sliders['triggerRadius'] ?? 40;
      default:
        return 0;
    }
  }

  /**
   * Get projectile speed (px/s) - direct from slider
   */
  static getProjectileSpeed(skill: CustomSkillV2): number {
    if (skill.deliveryType !== 'projectile') return 400;
    const s = skill.sliders as ProjectileSliders;
    return s.speed;
  }

  /**
   * Get projectile count (adet) - direct from slider
   */
  static getProjectileCount(skill: CustomSkillV2): number {
    if (skill.deliveryType !== 'projectile') return 1;
    const s = skill.sliders as ProjectileSliders;
    return s.projectileCount;
  }

  /**
   * Get pierce count (adet) - direct from slider
   */
  static getPierceCount(skill: CustomSkillV2): number {
    if (skill.deliveryType !== 'projectile') return 0;
    const s = skill.sliders as ProjectileSliders;
    return s.pierce;
  }

  /**
   * Get chain count (adet) - direct from slider
   */
  static getChainCount(skill: CustomSkillV2): number {
    if (skill.deliveryType !== 'projectile') return 0;
    const s = skill.sliders as ProjectileSliders;
    return s.chain;
  }

  /**
   * Get spread angle (degrees) - direct from slider
   */
  static getSpreadAngle(skill: CustomSkillV2): number {
    if (skill.deliveryType !== 'projectile') return 0;
    const s = skill.sliders as ProjectileSliders;
    return s.spreadAngle;
  }

  /**
   * Get projectile thickness (px) - direct from slider
   */
  static getProjectileThickness(skill: CustomSkillV2): number {
    if (skill.deliveryType !== 'projectile') return 10;
    const s = skill.sliders as ProjectileSliders;
    return s.thickness;
  }

  /**
   * Get trap count (adet) - direct from slider
   */
  static getTrapCount(skill: CustomSkillV2): number {
    if (skill.deliveryType !== 'trap') return 0;
    const s = skill.sliders as TrapSliders;
    return s.trapCount;
  }

  /**
   * Get arm time for traps (saniye) - direct from slider
   */
  static getArmTime(skill: CustomSkillV2): number {
    if (skill.deliveryType !== 'trap') return 0;
    const s = skill.sliders as TrapSliders;
    return s.armTime;
  }

  /**
   * Get totem count - always 1 (single totem design)
   */
  static getTotemCount(skill: CustomSkillV2): number {
    if (skill.deliveryType !== 'totem') return 0;
    return 1;
  }

  /**
   * Get totem HP - direct from slider
   */
  static getTotemHealth(skill: CustomSkillV2): number {
    if (skill.deliveryType !== 'totem') return 0;
    const s = skill.sliders as TotemSliders;
    return s.totemHealth;
  }

  /**
   * Get totem duration (saniye) - scales with totem health slider
   */
  static getTotemDuration(skill: CustomSkillV2): number {
    if (skill.deliveryType !== 'totem') return 10;
    const s = skill.sliders as TotemSliders;
    // Duration scales with health: more HP = longer lasting
    const healthNorm = getNormalizedSliderValue('totemHealth', s.totemHealth, 'totem');
    return 10 + healthNorm * 30; // 10s - 40s
  }

  /**
   * Get buff duration (saniye) - direct from slider
   */
  static getBuffDuration(skill: CustomSkillV2): number {
    if (skill.deliveryType !== 'buff') return 0;
    const s = skill.sliders as BuffSliders;
    return s.duration;
  }

  /**
   * Get buff/aura effect strength (% bonus) - direct from slider
   */
  static getEffectStrength(skill: CustomSkillV2): number {
    const sliders = skill.sliders as Record<string, number>;
    const strength = sliders['effectStrength'];
    if (strength === undefined) return 10;
    // Return as a multiplier: effectStrength is in %, convert to multiplier
    // e.g. effectStrength=10 -> 1.10 multiplier, effectStrength=25 -> 1.25 multiplier
    return 1 + strength / 100;
  }

  /**
   * Get attack speed multiplier - no longer a slider, returns fixed 1.0
   * Attack speed is now handled by cooldown slider / base cooldown
   */
  static getAttackSpeed(_skill: CustomSkillV2): number {
    return 1.0;
  }

  /**
   * Get cleave angle for melee (degrees) - melee no longer has cleave angle slider
   * Returns a fixed default value
   */
  static getCleaveAngle(_skill: CustomSkillV2): number {
    return 90; // Fixed 90 degree cleave
  }

  /**
   * Get AOE shape (circle or cone)
   */
  static getAOEShape(skill: CustomSkillV2): AOEShape {
    if (skill.deliveryType !== 'aoe') return 'circle';
    const s = skill.sliders as AOESliders;
    return s.shape || 'circle';
  }

  /**
   * Set AOE shape
   */
  static setAOEShape(skill: CustomSkillV2, shape: AOEShape): void {
    if (skill.deliveryType !== 'aoe') return;
    const s = skill.sliders as AOESliders;
    s.shape = shape;
    this.recalculateCosts(skill);
  }

  /**
   * Get cone angle for AOE cone shape (degrees) - direct from slider
   */
  static getAOEConeAngle(skill: CustomSkillV2): number {
    if (skill.deliveryType !== 'aoe') return 0;
    const s = skill.sliders as AOESliders;
    if (s.shape !== 'cone') return 0;
    return s.coneAngle;
  }

  /**
   * Get cone length for AOE cone shape (px)
   * Cone has 1.5x the circle radius
   */
  static getAOEConeLength(skill: CustomSkillV2): number {
    if (skill.deliveryType !== 'aoe') return 0;
    const s = skill.sliders as AOESliders;
    if (s.shape !== 'cone') return 0;
    const baseRadius = this.getEffectiveRadius(skill);
    return Math.floor(baseRadius * 1.5);
  }

  /**
   * Get AOE duration (saniye) - direct from slider
   */
  static getAOEDuration(skill: CustomSkillV2): number {
    if (skill.deliveryType !== 'aoe') return 0.5;
    const s = skill.sliders as AOESliders;
    return s.duration;
  }

  /**
   * Get AOE delay (saniye) - direct from slider
   */
  static getAOEDelay(skill: CustomSkillV2): number {
    if (skill.deliveryType !== 'aoe') return 0;
    const s = skill.sliders as AOESliders;
    return s.delay;
  }

  /**
   * Get beam width (px) - direct from slider
   */
  static getBeamWidth(skill: CustomSkillV2): number {
    if (skill.deliveryType !== 'beam') return 15;
    const s = skill.sliders as BeamSliders;
    return s.width;
  }

  /**
   * Get beam DPS (flat/s) - direct from slider + level scaling
   */
  static getBeamDPS(skill: CustomSkillV2): number {
    if (skill.deliveryType !== 'beam') return 15;
    const s = skill.sliders as BeamSliders;
    let dps = s.dps;
    // Level scaling
    dps *= 1 + (skill.level - 1) * 0.085;
    return dps;
  }

  // ========================
  // Curse Getters
  // ========================

  /**
   * Get curse power (% debuff strength) - direct from slider
   */
  static getCursePower(skill: CustomSkillV2): number {
    if (skill.deliveryType !== 'curse') return 0;
    const s = skill.sliders as CurseSliders;
    return s.power;
  }

  /**
   * Get curse duration (seconds) - direct from slider
   */
  static getCurseDuration(skill: CustomSkillV2): number {
    if (skill.deliveryType !== 'curse') return 0;
    const s = skill.sliders as CurseSliders;
    return s.duration;
  }

  /**
   * Get curse area radius (px) - direct from slider
   */
  static getCurseArea(skill: CustomSkillV2): number {
    if (skill.deliveryType !== 'curse') return 0;
    const s = skill.sliders as CurseSliders;
    return s.area;
  }

  // ========================
  // Minion Getters
  // ========================

  /**
   * Get minion health (HP) - direct from slider
   */
  static getMinionHealth(skill: CustomSkillV2): number {
    if (skill.deliveryType !== 'minion') return 0;
    const s = skill.sliders as MinionSliders;
    return s.minionHealth;
  }

  /**
   * Get minion damage (flat) - direct from slider
   */
  static getMinionDamage(skill: CustomSkillV2): number {
    if (skill.deliveryType !== 'minion') return 0;
    const s = skill.sliders as MinionSliders;
    return s.minionDamage;
  }

  /**
   * Get minion mana reserve (flat mana, NOT percentage) - direct from slider
   */
  static getMinionManaReserve(skill: CustomSkillV2): number {
    if (skill.deliveryType !== 'minion') return 0;
    const s = skill.sliders as MinionSliders;
    return s.manaReserve;
  }

  /**
   * Get beam mana drain (mana/s) - direct from slider
   */
  static getBeamManaDrain(skill: CustomSkillV2): number {
    if (skill.deliveryType !== 'beam') return 12;
    const s = skill.sliders as BeamSliders;
    return s.manaDrain;
  }

  /**
   * Get buff shield amount - direct from slider
   */
  static getBuffShield(skill: CustomSkillV2): number {
    if (skill.deliveryType !== 'buff') return 0;
    const s = skill.sliders as BuffSliders;
    return s.shield;
  }

  /**
   * Get buff shield decay rate - direct from slider
   */
  static getBuffShieldDecay(skill: CustomSkillV2): number {
    if (skill.deliveryType !== 'buff') return 0;
    const s = skill.sliders as BuffSliders;
    return s.shieldDecay;
  }

  /**
   * Get buff leech percent - direct from slider
   */
  static getBuffLeech(skill: CustomSkillV2): number {
    if (skill.deliveryType !== 'buff') return 0;
    const s = skill.sliders as BuffSliders;
    return s.leech;
  }

  /**
   * Get buff charges - direct from slider
   */
  static getBuffCharges(skill: CustomSkillV2): number {
    if (skill.deliveryType !== 'buff') return 0;
    const s = skill.sliders as BuffSliders;
    return s.charges;
  }

  /**
   * Get buff HP cost per second - direct from slider
   */
  static getBuffHpCost(skill: CustomSkillV2): number {
    if (skill.deliveryType !== 'buff') return 0;
    const s = skill.sliders as BuffSliders;
    return s.hpCost;
  }

  // ========================
  // Skill State Management
  // ========================

  static isReady(skill: CustomSkillV2): boolean {
    return skill.currentCooldown <= 0;
  }

  static canUse(skill: CustomSkillV2, currentMana: number, maxMana: number = 100): boolean {
    if (!this.isReady(skill)) return false;

    switch (skill.deliveryType) {
      case 'beam':
        // Need some mana to start channeling
        return currentMana >= (skill.manaDrainPerSecond || 12) * 0.5;
      case 'aura':
        // Check if enough mana to reserve
        return currentMana >= maxMana * (skill.manaReservePercent || 20) / 100;
      case 'minion':
        // Check if enough mana to reserve (flat)
        return currentMana >= (skill.manaReserveFlat || 20);
      default:
        return currentMana >= skill.manaCost;
    }
  }

  /**
   * Use a skill - returns mana cost
   */
  static use(skill: CustomSkillV2, cooldownMultiplier: number = 1.0, attackSpeedCooldown?: number): number {
    // Melee and projectile use attack speed instead of fixed cooldown
    if ((skill.deliveryType === 'melee' || skill.deliveryType === 'projectile') && attackSpeedCooldown !== undefined) {
      skill.currentCooldown = attackSpeedCooldown * cooldownMultiplier;
    } else {
      skill.currentCooldown = skill.cooldown * cooldownMultiplier;
    }

    // Return mana cost based on delivery type
    switch (skill.deliveryType) {
      case 'beam':
        // Beam doesn't have upfront cost, just drain per second
        return 0;
      case 'aura':
        // Aura reserves mana, doesn't spend it
        return 0;
      case 'minion':
        // Minion reserves flat mana, doesn't spend it
        return 0;
      default:
        return skill.manaCost;
    }
  }

  static update(skill: CustomSkillV2, deltaTime: number): void {
    if (skill.currentCooldown > 0) {
      skill.currentCooldown = Math.max(0, skill.currentCooldown - deltaTime);
    }
  }

  static getCooldownPercent(skill: CustomSkillV2): number {
    if (skill.currentCooldown <= 0 || skill.cooldown <= 0) return 0;
    return skill.currentCooldown / skill.cooldown;
  }

  /**
   * Add XP to a skill. Returns true if the skill leveled up.
   */
  static addXP(skill: CustomSkillV2, amount: number): boolean {
    skill.xp += amount;
    let didLevelUp = false;
    while (skill.xp >= skill.maxXp) {
      skill.xp -= skill.maxXp;
      skill.level++;
      skill.maxXp = Math.floor(10 * Math.pow(1.5, skill.level - 1));
      didLevelUp = true;
    }
    return didLevelUp;
  }

  // ========================
  // Socket Management
  // ========================

  static socketStone(skill: CustomSkillV2, slotIndex: number, stone: EffectStone): EffectStone | null {
    if (slotIndex < 0 || slotIndex >= skill.socketCount) return null;

    const previous = skill.sockets[slotIndex];
    skill.sockets[slotIndex] = stone;
    this.recalculateCosts(skill);
    return previous;
  }

  static unsocketStone(skill: CustomSkillV2, slotIndex: number): EffectStone | null {
    if (slotIndex < 0 || slotIndex >= skill.socketCount) return null;

    const stone = skill.sockets[slotIndex];
    skill.sockets[slotIndex] = null;
    this.recalculateCosts(skill);
    return stone;
  }

  // ========================
  // Serialization
  // ========================

  static serialize(skill: CustomSkillV2): object {
    // Clone sliders and ensure shape is included for AOE skills
    const slidersClone = { ...skill.sliders };

    return {
      id: skill.id,
      name: skill.name,
      version: skill.version,
      deliveryType: skill.deliveryType,
      element: skill.element,
      sliders: slidersClone,
      level: skill.level,
      xp: skill.xp || 0,
      maxXp: skill.maxXp || 10,
      sockets: skill.sockets.map(s => s ? {
        id: s.id,
        name: s.name,
        type: s.type,
        rarity: s.rarity,
        effect: { ...s.effect },
      } : null),
      // Choice fields
      meleeType: skill.meleeType,
      projectileContact: skill.projectileContact,
      aoeTrigger: skill.aoeTrigger,
      aoePosition: skill.aoePosition,
      beamMode: skill.beamMode,
      buffType: skill.buffType,
      trapTrigger: skill.trapTrigger,
      totemType: skill.totemType,
      curseType: skill.curseType,
      curseTarget: skill.curseTarget,
      minionType: skill.minionType,
    };
  }

  static deserialize(data: Record<string, unknown>): CustomSkillV2 | null {
    if (!data || data.version !== 2 || !data.deliveryType || !data.element) {
      return null;
    }

    const deliveryType = data.deliveryType as SkillDeliveryType;
    const element = data.element as ElementType;
    const name = (data.name as string) || 'Skill';

    // Create a fresh skill with defaults, then apply saved sliders
    const skill = this.createSkill(deliveryType, element, name);

    // Restore saved properties
    skill.id = (data.id as string) || skill.id;
    skill.level = (data.level as number) || 1;
    skill.xp = (data.xp as number) || 0;
    skill.maxXp = (data.maxXp as number) || Math.floor(10 * Math.pow(1.5, skill.level - 1));

    // Restore slider values
    if (data.sliders && typeof data.sliders === 'object') {
      const savedSliders = data.sliders as Record<string, unknown>;
      for (const key of Object.keys(skill.sliders)) {
        if (key in savedSliders && typeof savedSliders[key] === 'number') {
          (skill.sliders as Record<string, number>)[key] = savedSliders[key] as number;
        }
      }
      // Restore AOE shape if present
      if ('shape' in savedSliders) {
        (skill.sliders as Record<string, unknown>).shape = savedSliders.shape;
      }
    }

    // Recalculate costs based on restored sliders
    this.recalculateCosts(skill);

    // Restore choice fields
    if (data.meleeType) skill.meleeType = data.meleeType as any;
    if (data.projectileContact) skill.projectileContact = data.projectileContact as any;
    if (data.aoeTrigger) skill.aoeTrigger = data.aoeTrigger as any;
    if (data.aoePosition) skill.aoePosition = data.aoePosition as any;
    if (data.beamMode) skill.beamMode = data.beamMode as any;
    if (data.buffType) skill.buffType = data.buffType as any;
    if (data.trapTrigger) skill.trapTrigger = data.trapTrigger as any;
    if (data.totemType) skill.totemType = data.totemType as any;
    if (data.curseType) skill.curseType = data.curseType as any;
    if (data.curseTarget) skill.curseTarget = data.curseTarget as any;
    if (data.minionType) skill.minionType = data.minionType as any;

    // Restore sockets
    if (data.sockets && Array.isArray(data.sockets)) {
      skill.sockets = (data.sockets as unknown[]).map((s: unknown) => {
        if (!s || typeof s === 'string') return null; // Legacy format (just ID)
        const sock = s as Record<string, unknown>;
        return {
          id: (sock.id || '') as string,
          name: (sock.name || '') as string,
          nameKey: (sock.nameKey || '') as string,
          type: (sock.type || 'od') as import('./SkillCreator').EffectStone['type'],
          rarity: (sock.rarity || 'normal') as import('./SkillCreator').EffectStone['rarity'],
          effect: (sock.effect || {}) as import('./SkillCreator').EffectStoneEffect,
        };
      });
    }

    return skill;
  }

  static clone(skill: CustomSkillV2): CustomSkillV2 {
    return {
      ...skill,
      id: `skill_v2_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`,
      sliders: { ...skill.sliders },
      sockets: [...skill.sockets],
      currentCooldown: 0,
      xp: 0,
      maxXp: 10,
      isActive: false,
    };
  }

  // ========================
  // UI Helpers
  // ========================

  static getSliderConfigs(deliveryType: SkillDeliveryType) {
    return DELIVERY_TYPE_SLIDERS[deliveryType];
  }

  static getManaDisplayText(skill: CustomSkillV2): string {
    switch (skill.deliveryType) {
      case 'beam':
        return `${skill.manaDrainPerSecond}/sn`;
      case 'aura':
        return `${skill.manaReservePercent}% Rezerv`;
      case 'minion':
        return `${skill.manaReserveFlat} Rezerv`;
      default:
        return skill.manaCost.toString();
    }
  }

  static getCooldownDisplayText(skill: CustomSkillV2): string {
    if (skill.deliveryType === 'beam') {
      return 'Kanalize';
    }
    if (skill.deliveryType === 'aura' || skill.deliveryType === 'minion') {
      return 'Açık/Kapalı';
    }
    return `${skill.cooldown.toFixed(1)}s`;
  }
}
